import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { Document, pdfjs } from "react-pdf";
import { LucideChevronLeft, LucideLoader2 } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from "@/constants/api";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/layouts/PageHeader";
import ErrorBoundary from "@/components/ui/ErrorBoundary";
import { getTemplateUrl } from "@/reports/registry";
import { LogoCanvas } from "@/components/branding/LogoCanvas";
import { LogoUploader } from "@/components/branding/LogoUploader";
import { invalidateFolderLogos } from "@/utils/imageCache";

// react-pdf worker (idempotent — also set by PDFPreview).
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const MAX_LOGOS = 3;
const PAGE_WIDTH = 520;

// Default geometry (PDF points, bottom-left origin) for a freshly added logo.
const newLogoGeom = (page) => ({ page, x: 40, y: 690, width: 110, height: 55 });

export const Branding = () => {
  const { reportFolderId } = useParams();
  const { getToken, userId } = useAuth();
  const navigate = useNavigate();

  const [folderName, setFolderName] = useState("");
  const [templateUrl, setTemplateUrl] = useState(null);
  const [logos, setLogos] = useState([]);
  const [numPages, setNumPages] = useState(1);
  const [activePage, setActivePage] = useState(0);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const token = await getToken();
        const [folderRes, logosRes] = await Promise.all([
          fetch(`${BACKEND_URL}api/report-folders/${reportFolderId}`, {
            headers: { Authorization: `Bearer ${token}` }, signal: ac.signal,
          }),
          fetch(`${BACKEND_URL}api/report-folders/${reportFolderId}/logos`, {
            headers: { Authorization: `Bearer ${token}` }, signal: ac.signal,
          }),
        ]);
        if (!folderRes.ok) throw new Error("Failed to load Report Folder");
        const folder = await folderRes.json();
        const url = getTemplateUrl(folder.reportType);
        if (!url) throw new Error(`No template for ${folder.reportType}`);
        setFolderName(folder.name || "");
        setTemplateUrl(url);
        if (logosRes.ok) {
          const { logos: existing } = await logosRes.json();
          setLogos(existing ?? []);
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        setError(err.message);
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportFolderId]);

  const handleAdd = useCallback((dataUri) => {
    setLogos((prev) => {
      if (prev.length >= MAX_LOGOS) {
        toast.error(`You can add at most ${MAX_LOGOS} logos.`);
        return prev;
      }
      const next = [...prev, { data: dataUri, ...newLogoGeom(activePage) }];
      setSelected(next.length - 1);
      return next;
    });
  }, [activePage]);

  // newGeom === null removes the logo.
  const handleChange = useCallback((idx, newGeom) => {
    setLogos((prev) => {
      if (newGeom === null) {
        setSelected(null);
        return prev.filter((_, i) => i !== idx);
      }
      return prev.map((logo, i) => (i === idx ? { ...logo, ...newGeom } : logo));
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/report-folders/${reportFolderId}/logos`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ logos }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to save logos");
      }
      await invalidateFolderLogos(userId, reportFolderId);
      toast.success("Branding saved.");
      navigate(`/report-folders/${reportFolderId}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="container my-6 text-muted-foreground">Loading…</div>;
  if (error) return <div className="container my-6 text-destructive">Error: {error}</div>;

  return (
    <div className="container my-6">
      <Link
        to={`/report-folders/${reportFolderId}`}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <LucideChevronLeft size={14} /> Back to Report Folder
      </Link>

      <PageHeader
        title="Branding"
        subtitle={`Add up to ${MAX_LOGOS} logos and position them on ${folderName || "this folder"}'s report pages. Drag to move, drag the corner to resize.`}
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <LogoUploader onAdd={handleAdd} disabled={logos.length >= MAX_LOGOS} />
        {numPages > 1 && (
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            Add new logo to:
            <select
              value={activePage}
              onChange={(e) => setActivePage(Number(e.target.value))}
              className="rounded-md border border-border bg-card px-2 py-1"
            >
              {Array.from({ length: numPages }, (_, i) => (
                <option key={i} value={i}>Page {i + 1}</option>
              ))}
            </select>
          </label>
        )}
        <span className="text-sm text-muted-foreground">{logos.length}/{MAX_LOGOS} logos</span>
        <div className="ml-auto">
          <Button onClick={handleSave} disabled={saving}>
            {saving && <LucideLoader2 className="mr-2 animate-spin" size={16} />}
            Save branding
          </Button>
        </div>
      </div>

      <ErrorBoundary>
        <div
          className="flex flex-col items-center gap-6 rounded-xl bg-muted p-6"
          onPointerDown={() => setSelected(null)}
        >
          <Document
            file={templateUrl}
            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            loading={<div className="text-muted-foreground">Loading template…</div>}
          >
            {Array.from({ length: numPages }, (_, i) => (
              <LogoCanvas
                key={i}
                pageNumber={i + 1}
                pageIndex={i}
                width={PAGE_WIDTH}
                logos={logos}
                selected={selected}
                onSelect={setSelected}
                onChange={handleChange}
              />
            ))}
          </Document>
        </div>
      </ErrorBoundary>
    </div>
  );
};
